import { Exchange, makeOperation, Operation } from "@urql/core";
import { Kind } from "graphql";
import { map, pipe } from "wonka";

export const changedAtVariableName = "changedAt";

/**
 * Returns ISO timestamps that strictly increase, even within the same millisecond, so the server can order
 * changes of the same field (last write wins per field, see ADR-11).
 */
export function createChangedAtClock(now: () => number = Date.now) {
  let last = 0;
  return (): string => {
    last = Math.max(now(), last + 1);
    return new Date(last).toISOString();
  };
}

function declaresChangedAtVariable(operation: Operation): boolean {
  return operation.query.definitions.some(
    (definition) =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      (definition.variableDefinitions ?? []).some(
        (v) => v.variable.name.value === changedAtVariableName,
      ),
  );
}

/**
 * Sets `changedAt` on every mutation that declares it, at the moment the mutation is created. Must run before the
 * `offlineExchange`, so the value is stored with a queued mutation and kept when the queue is replayed.
 */
export const changedAtExchange =
  (nextChangedAt: () => string = createChangedAtClock()): Exchange =>
  ({ forward }) =>
  (operations$) =>
    forward(
      pipe(
        operations$,
        map((operation) =>
          operation.kind === "mutation" &&
          operation.variables?.[changedAtVariableName] == null &&
          declaresChangedAtVariable(operation)
            ? makeOperation(
                operation.kind,
                {
                  ...operation,
                  variables: {
                    ...operation.variables,
                    [changedAtVariableName]: nextChangedAt(),
                  },
                },
                operation.context,
              )
            : operation,
        ),
      ),
    );
